import { lookbookApi, type Lookbook } from "@/api/lookbook-api";
import { useWorkspaceRole } from "@/hooks/use-workspace-role";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookMarked,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import posthog from "posthog-js";
import { getErrorMessage } from "@/lib/api-error";
import { LookbookAccessSettings } from "./lookbook-access-settings";

/**
 * Lookbook management — a top-level page rather than a settings pane, because
 * lookbooks are worked on alongside the pipeline (deals are filed into them
 * from there) rather than configured once and forgotten.
 */
export default function Lookbooks() {
  const { workspace, isLoading, activeWorkspaceId, invalidateWorkspace } =
    useWorkspaceSettings();
  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();
  const workspaceId = urlWorkspaceId || activeWorkspaceId;
  const { isWorkspaceOwner } = useWorkspaceRole();
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(
    null
  );
  const [pendingDelete, setPendingDelete] = useState<Lookbook | null>(null);

  useEffect(() => {
    if (!workspace) return;
    setEnabled(workspace.lookbooks_enabled || false);
  }, [workspace]);

  const lookbooksKey = ["lookbooks", activeWorkspaceId] as const;

  // Listing is open to any workspace member — the page is read-only for them,
  // and every mutation below is rendered owner-only rather than merely
  // disabled, because the endpoints behind them are manager-only.
  const { data: lookbooks = [], isLoading: isLoadingLookbooks } = useQuery({
    queryKey: lookbooksKey,
    queryFn: () => lookbookApi.list(activeWorkspaceId as string),
    enabled: !!activeWorkspaceId && enabled,
  });

  const refreshLookbooks = () =>
    queryClient.invalidateQueries({ queryKey: lookbooksKey });

  const handleToggle = async (checked: boolean) => {
    if (!activeWorkspaceId) return;
    setIsToggling(true);
    try {
      await lookbookApi.toggleEnabled(activeWorkspaceId, checked);
      setEnabled(checked);
      invalidateWorkspace();
      await refreshLookbooks();
      toast.success(
        checked
          ? "Lookbooks enabled"
          : "Lookbooks disabled — shared links now show nothing"
      );
      posthog.capture("settings_lookbooks_toggled", {
        workspace_id: activeWorkspaceId,
        enabled: checked,
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to toggle Lookbooks"));
    } finally {
      setIsToggling(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: () => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return lookbookApi.create(
        activeWorkspaceId,
        `Lookbook ${lookbooks.length + 1}`
      );
    },
    onSuccess: async (lookbook) => {
      await refreshLookbooks();
      // Drop straight into rename: a lookbook is identified by its name when
      // you're picking one from the pipeline, and "Lookbook 4" tells you
      // nothing there.
      setRenaming({ id: lookbook._id, name: lookbook.name });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create lookbook"));
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return lookbookApi.rename(activeWorkspaceId, id, name);
    },
    onSuccess: async () => {
      setRenaming(null);
      await refreshLookbooks();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to rename lookbook"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return lookbookApi.remove(activeWorkspaceId, id);
    },
    onSuccess: async () => {
      setPendingDelete(null);
      await refreshLookbooks();
      toast.success("Lookbook deleted");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete lookbook"));
    },
  });

  const lookbookUrl = (publicId: string) =>
    `${window.location.origin}/lb/${publicId}`;

  const copyUrl = (publicId: string) => {
    navigator.clipboard.writeText(lookbookUrl(publicId));
    toast.success("Copied to clipboard");
  };

  const submitRename = () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    if (!name) {
      setRenaming(null);
      return;
    }
    renameMutation.mutate({ id: renaming.id, name });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-serif text-3xl text-foreground sm:text-4xl">
            <BookMarked className="h-7 w-7 text-muted-foreground" />
            Lookbooks
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Private raiseboards — a hand-picked set of companies, shared by
            link, optionally behind a password. It is never listed publicly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="flex h-8 items-center rounded-sm border-border bg-card px-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
          >
            {enabled ? "On" : "Off"}
          </Badge>
          {/* Whether the workspace publishes lookbooks at all is an owner
              decision, and the endpoint behind it is manager-only. Members see
              the state, they just can't flip it. */}
          {isWorkspaceOwner && (
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={isToggling}
              aria-label="Enable lookbooks"
            />
          )}
        </div>
      </div>

      {!enabled ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
          {isWorkspaceOwner
            ? "Turn Lookbooks on to group companies into named collections and share each one at its own private link."
            : "Lookbooks are turned off for this workspace. Ask an owner to turn them on to group companies into named collections shared by link."}
        </div>
      ) : (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
              {isWorkspaceOwner ? "Your Lookbooks" : "Workspace Lookbooks"}
            </h2>
            {isWorkspaceOwner && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                New lookbook
              </Button>
            )}
          </div>

          {isLoadingLookbooks ? (
            <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-card">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : lookbooks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
              {isWorkspaceOwner ? (
                <>
                  No lookbooks yet. Create one, then add companies to it from
                  the{" "}
                  <Link
                    to={`/${workspaceId}/deals`}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Pipeline
                  </Link>
                  .
                </>
              ) : (
                "No lookbooks yet. Only workspace owners can create one."
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {lookbooks.map((lookbook) => (
                <div
                  key={lookbook._id}
                  className="rounded-lg border border-border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    {renaming?.id === lookbook._id ? (
                      <div className="flex flex-1 items-center gap-2">
                        <Input
                          autoFocus
                          value={renaming.name}
                          maxLength={80}
                          onChange={(e) =>
                            setRenaming({ id: lookbook._id, name: e.target.value })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submitRename();
                            if (e.key === "Escape") setRenaming(null);
                          }}
                          className="h-8 flex-1 text-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={submitRename}
                          disabled={renameMutation.isPending}
                          title="Save name"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => setRenaming(null)}
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="min-w-0">
                          {/* The name doubles as the rename affordance, so a
                              member gets it as plain text rather than a button
                              that opens an editor the server would reject. */}
                          {isWorkspaceOwner ? (
                            <button
                              type="button"
                              className="truncate text-sm font-medium underline-offset-4 hover:underline"
                              onClick={() =>
                                setRenaming({
                                  id: lookbook._id,
                                  name: lookbook.name,
                                })
                              }
                              title="Rename"
                            >
                              {lookbook.name}
                            </button>
                          ) : (
                            <p className="truncate text-sm font-medium">
                              {lookbook.name}
                            </p>
                          )}
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {lookbook.deal_count}{" "}
                            {lookbook.deal_count === 1 ? "company" : "companies"}
                          </p>
                        </div>
                        {isWorkspaceOwner && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                            onClick={() => setPendingDelete(lookbook)}
                            title="Delete lookbook"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="mt-4 border-t border-border pt-4">
                    <Label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                      Private URL
                    </Label>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        readOnly
                        value={lookbookUrl(lookbook.public_id)}
                        className="flex-1 bg-muted font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => copyUrl(lookbook.public_id)}
                        title="Copy URL"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() =>
                          window.open(lookbookUrl(lookbook.public_id), "_blank")
                        }
                        title="Open in new tab"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {lookbook.password_protected
                        ? "Anyone with this link and the password can browse the lookbook."
                        : "Anyone with this link can browse the lookbook."}{" "}
                      The link can't be changed once shared.
                    </p>
                  </div>

                  <LookbookAccessSettings
                    lookbook={lookbook}
                    workspaceId={activeWorkspaceId ?? undefined}
                    canEdit={isWorkspaceOwner}
                    onChanged={refreshLookbooks}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Cross-page breadcrumb. Owner-only: filing a deal into a lookbook is a
          manager-only endpoint, so the Pipeline never offers a member the
          "Add to board" lookbook rows this points them at. */}
      {enabled && isWorkspaceOwner && (
        <div className="mt-8 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-sm">
          <p className="text-muted-foreground">
            Add companies to a lookbook from the{" "}
            <span className="font-medium text-foreground">Pipeline</span> —
            select them, or open a deal's menu, then use{" "}
            <span className="font-medium text-foreground">Add to board</span>{" "}
            and pick the lookbook. Filing a deal into a lookbook also publishes
            it to your Raiseboard, which lookbooks draw from.
          </p>
          <Link
            to={`/${workspaceId}/deals`}
            className="mt-1 inline-block text-xs font-medium text-foreground underline-offset-4 hover:underline"
          >
            Open Pipeline →
          </Link>
        </div>
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lookbook?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{pendingDelete?.name}</span> and its
              link will stop working immediately, and any upvotes or comments
              left inside it will be deleted. The companies themselves stay on
              your Raiseboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) deleteMutation.mutate(pendingDelete._id);
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
